import { useState } from 'react'

import { BackedByLineChart } from './components/BackedByLineChart'
import { BackedByLineChartDetailed } from './components/BackedByLineChartDetailed'
import { BackedByPieChart } from './components/BackedByPieChart'
import { BackedByPieChartDetailed } from './components/BackedByPieChartDetailed'
import { useBackedByChartDetailed } from './logic/useBackedByChartDetailed'
import { BackedByChartItem } from './types'
import { Token } from '@/domain/types/Token'
import { ChartTabComponentProps } from '@/ui/charts/components/ChartTabsPanel'
import { cn } from '@/ui/utils/style'

export interface BackedByChartProps extends ChartTabComponentProps {
  items: BackedByChartItem[]
  underlyingToken: Token
  groupUsdsDetails: boolean
  detailsExpanded: boolean
  onDetailsExpandedChange: (open: boolean) => void
}

export function BackedByChart({
  items,
  underlyingToken,
  groupUsdsDetails,
  detailsExpanded,
  onDetailsExpandedChange,
}: BackedByChartProps) {
  if (groupUsdsDetails) {
    return (
      <BackedByChartGrouped
        items={items}
        underlyingToken={underlyingToken}
        detailsExpanded={detailsExpanded}
        onDetailsExpandedChange={onDetailsExpandedChange}
      />
    )
  }

  return <BackedByChartSimple items={items} underlyingToken={underlyingToken} />
}

function BackedByChartSimple({ items, underlyingToken }: { items: BackedByChartItem[]; underlyingToken: Token }) {
  const [highlightedIndex, setHighlightedIndex] = useState<number | undefined>(undefined)
  return (
    <div
      className={cn(
        'grid grid-rows-1 items-start gap-4 md:items-center',
        'sm:grid-cols-[4fr_5fr] sm:grid-rows-1 sm:gap-8 sm:p-4',
        'xl:grid-cols-[2fr_3fr] xl:gap-20 xl:px-14 xl:py-6',
      )}
    >
      <BackedByPieChart
        className="hidden md:block"
        items={items}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        underlyingToken={underlyingToken}
      />
      <BackedByLineChart
        items={items}
        highlightedIndex={highlightedIndex}
        setHighlightedIndex={setHighlightedIndex}
        underlyingToken={underlyingToken}
      />
    </div>
  )
}

function BackedByChartGrouped({
  items,
  underlyingToken,
  detailsExpanded,
  onDetailsExpandedChange,
}: {
  items: BackedByChartItem[]
  underlyingToken: Token
  detailsExpanded: boolean
  onDetailsExpandedChange: (open: boolean) => void
}) {
  const chartResult = useBackedByChartDetailed({
    items,
    underlyingToken,
    detailsExpanded,
    onDetailsExpandedChange,
  })

  return (
    <div
      className={cn(
        'grid gap-4',
        'grid-rows-1 items-start md:items-center',
        'sm:grid-cols-[4fr_5fr] sm:grid-rows-1 sm:gap-8 sm:p-4',
        'xl:grid-cols-[2fr_3fr] xl:gap-20 xl:px-14 xl:py-6',
      )}
    >
      <BackedByPieChartDetailed
        className="hidden md:block"
        displayedItems={chartResult.displayedItems}
        highlightedKey={chartResult.highlightedKey}
        setHighlightedKey={chartResult.setHighlightedKey}
        totalAmount={chartResult.totalAmount}
        ticker={chartResult.ticker}
        pieChartData={chartResult.pieChartData}
        getHighlightedItem={chartResult.getHighlightedItem}
        getPieSliceColor={chartResult.getPieSliceColor}
        onSliceClick={chartResult.handleSliceClick}
      />
      <BackedByLineChartDetailed
        items={chartResult.items}
        highlightedKey={chartResult.highlightedKey}
        setHighlightedKey={chartResult.setHighlightedKey}
        underlyingToken={underlyingToken}
        handleDetailsExpandedChange={chartResult.handleDetailsExpandedChange}
        getLineChartItemData={chartResult.getLineChartItemData}
        getDetailItemData={chartResult.getDetailItemData}
      />
    </div>
  )
}
